import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { Input, RadioGroup, RadioGroupItem } from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { FieldGrid } from '../../section-shell';
import type { ContractFormValues } from '../../contract-form';
import { DateInput } from '@/components/date-input';

/** Register purpose — individual (canhan) or business (congty). */
const FOR_OPTIONS = [
  { value: 'canhan', label: 'Cá Nhân' },
  { value: 'congty', label: 'Doanh Nghiệp' },
] as const;

interface VnnicDeclaresFieldsProps {
  /** Absolute path prefix: `products.{i}.service_domain_metadata`. */
  metaPath: string;
}

/**
 * Step 2 of the VNNIC registrar form: declares / UI (người khai báo) information.
 * Writes into `service_domain_metadata.domain_extra_fields.ui*` fields.
 * Mirrors legacy DeclaresInformation component (for radio, uiName, ID/tax,
 * address, email, phone, fax, gender, birthdate, company, position).
 * Address is plain text (no PermanentAddress lookup widget in this workspace).
 * Gender/birthdate fields present for canhan; company/tax for congty.
 */
export function VnnicDeclaresFields({ metaPath }: VnnicDeclaresFieldsProps) {
  const { control, setValue } = useFormContext<ContractFormValues>();
  const extraPath = `${metaPath}.domain_extra_fields`;

  const forRegister = (useWatch({ control, name: `${extraPath}.for` as never }) as string) ?? 'canhan';

  const field = (key: string) => `${extraPath}.${key}` as never;

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
      {/* Purpose of registration: individual vs business (mirrors owner step) */}
      <Field label="Mục đích đăng ký">
        {() => (
          <RadioGroup
            value={forRegister}
            onValueChange={(v) => setValue(field('for'), v as never, { shouldDirty: true })}
            style={{ display: 'flex', gap: 'var(--vn-space-3)' }}
          >
            {FOR_OPTIONS.map((opt) => (
              <label
                key={opt.value}
                style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)', cursor: 'pointer', fontSize: 'var(--vn-font-size-sm)' }}
              >
                <RadioGroupItem value={opt.value} />
                {opt.label}
              </label>
            ))}
          </RadioGroup>
        )}
      </Field>

      {/* Declarant name */}
      <Controller
        control={control}
        name={field('uiName')}
        render={({ field: f }) => (
          <Field label="Họ và tên người khai báo">
            {(p) => (
              <Input {...p} type="text" placeholder="Họ và tên"
                value={(f.value as string) ?? ''} onChange={f.onChange} onBlur={f.onBlur} name={f.name} ref={f.ref} />
            )}
          </Field>
        )}
      />

      {/* Individual: ID number; business: tax code */}
      {forRegister === 'canhan' ? (
        <Controller
          control={control}
          name={field('uiID_Number')}
          render={({ field: f }) => (
            <Field label="Số CMND / CCCD">
              {(p) => (
                <Input {...p} type="text" placeholder="Số CMND / CCCD"
                  value={(f.value as string) ?? ''} onChange={f.onChange} onBlur={f.onBlur} name={f.name} ref={f.ref} />
              )}
            </Field>
          )}
        />
      ) : (
        <Controller
          control={control}
          name={field('uiTaxCode')}
          render={({ field: f }) => (
            <Field label="Mã số thuế">
              {(p) => (
                <Input {...p} type="text" placeholder="Mã số thuế"
                  value={(f.value as string) ?? ''} onChange={f.onChange} onBlur={f.onBlur} name={f.name} ref={f.ref} />
              )}
            </Field>
          )}
        />
      )}

      {/* Province / address */}
      <Controller
        control={control}
        name={field('uiProvince')}
        render={({ field: f }) => (
          <Field label="Tỉnh / Thành phố">
            {(p) => (
              <Input {...p} type="text" placeholder="Tỉnh / Thành phố"
                value={(f.value as string) ?? ''} onChange={f.onChange} onBlur={f.onBlur} name={f.name} ref={f.ref} />
            )}
          </Field>
        )}
      />
      <Controller
        control={control}
        name={field('uiAddress')}
        render={({ field: f }) => (
          <Field label="Địa chỉ">
            {(p) => (
              <Input {...p} type="text" placeholder="Số nhà, đường, phường/xã"
                value={(f.value as string) ?? ''} onChange={f.onChange} onBlur={f.onBlur} name={f.name} ref={f.ref} />
            )}
          </Field>
        )}
      />

      <FieldGrid min={200}>
        <Controller
          control={control}
          name={field('uiEmail')}
          render={({ field: f }) => (
            <Field label="Email">
              {(p) => (
                <Input {...p} type="email" placeholder="email@example.com"
                  value={(f.value as string) ?? ''} onChange={f.onChange} onBlur={f.onBlur} name={f.name} ref={f.ref} />
              )}
            </Field>
          )}
        />
        <Controller
          control={control}
          name={field('uiPhone')}
          render={({ field: f }) => (
            <Field label="Điện thoại">
              {(p) => (
                <Input {...p} type="text" placeholder="0901234567"
                  value={(f.value as string) ?? ''} onChange={f.onChange} onBlur={f.onBlur} name={f.name} ref={f.ref} />
              )}
            </Field>
          )}
        />
        <Controller
          control={control}
          name={field('uiFax')}
          render={({ field: f }) => (
            <Field label="Fax">
              {(p) => (
                <Input {...p} type="text" placeholder="Số fax"
                  value={(f.value as string) ?? ''} onChange={f.onChange} onBlur={f.onBlur} name={f.name} ref={f.ref} />
              )}
            </Field>
          )}
        />

        {/* Gender — only for individual */}
        {forRegister === 'canhan' && (
          <Controller
            control={control}
            name={field('uiGender')}
            render={({ field: f }) => (
              <Field label="Giới tính">
                {(p) => (
                  <Input {...p} type="text" placeholder="Nam / Nữ"
                    value={(f.value as string) ?? ''} onChange={f.onChange} onBlur={f.onBlur} name={f.name} ref={f.ref} />
                )}
              </Field>
            )}
          />
        )}

        {/* Birthdate — only for individual */}
        {forRegister === 'canhan' && (
          <Controller
            control={control}
            name={field('uiBirthdate')}
            render={({ field: f }) => (
              <Field label="Ngày sinh">
                {(p) => (
                  <DateInput {...p}
                    value={(f.value as string) ?? ''} onChange={f.onChange} onBlur={f.onBlur} name={f.name} ref={f.ref} />
                )}
              </Field>
            )}
          />
        )}

        <Controller
          control={control}
          name={field('uiCompany')}
          render={({ field: f }) => (
            <Field label="Công ty">
              {(p) => (
                <Input {...p} type="text" placeholder="Tên công ty"
                  value={(f.value as string) ?? ''} onChange={f.onChange} onBlur={f.onBlur} name={f.name} ref={f.ref} />
              )}
            </Field>
          )}
        />
        <Controller
          control={control}
          name={field('uiPosition')}
          render={({ field: f }) => (
            <Field label="Chức vụ">
              {(p) => (
                <Input {...p} type="text" placeholder="Chức vụ"
                  value={(f.value as string) ?? ''} onChange={f.onChange} onBlur={f.onBlur} name={f.name} ref={f.ref} />
              )}
            </Field>
          )}
        />
      </FieldGrid>
    </div>
  );
}
